import React from "react";
import { Link } from "react-router-dom";
import { Calculator, FileText, ShieldCheck, Landmark, Scale, TrendingUp, RefreshCw, ShieldAlert, ArrowRight } from "lucide-react";
import { heroDesk, aboutImages } from "../mock";
import { translations, serviceIconNames } from "../translations";
import { useLanguage } from "../context/LanguageContext";
import Reveal from "../components/Reveal";

const ICONS = { Calculator, FileText, ShieldCheck, Landmark, Scale, TrendingUp, RefreshCw, ShieldAlert };

const Hero = ({ t }) => (
  <section className="relative bg-[#eef1f3] overflow-hidden">
    <div className="max-w-[1280px] mx-auto lg:grid lg:grid-cols-12 items-stretch">
      <div className="relative z-10 lg:col-span-5 px-5 lg:px-8 py-14 lg:py-24">
        <div className="relative">
          <div className="hidden lg:block absolute -top-5 -left-5 w-40 h-40 border-2 border-sky-accent" />
          <div className="relative bg-steel text-white p-9 lg:p-12 shadow-xl">
            <h1 className="font-serif text-3xl lg:text-[2.6rem] leading-tight font-bold">{t.home.heroTitle}</h1>
            <ul className="mt-8 space-y-4">
              {t.home.heroBullets.map((b) => (
                <li key={b} className="flex items-center gap-3 text-lg lg:text-xl font-serif">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/80 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            <Link to="/our-services" className="pill-btn mt-10">{t.ui.findOut}</Link>
          </div>
        </div>
      </div>
      <div className="lg:col-span-7 relative min-h-[320px] lg:min-h-[560px]">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${heroDesk})` }} />
      </div>
    </div>
  </section>
);

const HomeServices = ({ t }) => (
  <section className="py-20 bg-white">
    <div className="max-w-[1280px] mx-auto px-5 lg:px-8">
      <Reveal className="text-center">
        <h2 className="text-3xl md:text-4xl font-bold brand-navy dash-center">{t.home.servicesTitle}</h2>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
        {t.services.map((s, i) => {
          const Icon = ICONS[serviceIconNames[s.id]] || ShieldCheck;
          return (
            <Reveal key={s.id} delay={(i % 4) * 80}>
              <Link to="/our-services" className="group block bg-[#f6f8f6] rounded-sm p-7 h-full border border-transparent hover:border-gold hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-steel text-white flex items-center justify-center group-hover:bg-sky-accent transition-colors">
                  <Icon size={22} />
                </div>
                <h3 className="font-serif text-lg font-bold brand-navy mt-5 leading-snug">{s.title}</h3>
                <p className="text-slate-600 text-sm mt-3 leading-relaxed">{s.short}</p>
              </Link>
            </Reveal>
          );
        })}
      </div>
      <div className="text-center mt-12">
        <Link to="/our-services" className="pill-btn">{t.ui.knowMore}</Link>
      </div>
    </div>
  </section>
);

const ContactCTA = ({ t }) => (
  <section className="relative py-28 overflow-hidden">
    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${aboutImages.primary})` }} />
    <div className="absolute inset-0 bg-[#1d4927]/85" />
    <div className="relative z-10 max-w-[1280px] mx-auto px-5 lg:px-8">
      <Reveal>
        <h2 className="text-4xl md:text-5xl font-bold text-white">{t.home.ctaTitle}</h2>
        <p className="text-white/80 mt-4 text-lg">{t.home.ctaText}</p>
        <Link to="/contact-us" className="pill-btn-outline mt-9">{t.ui.getInTouch}</Link>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  return (
    <div>
      <Hero t={t} />
      <HomeServices t={t} />
      <ContactCTA t={t} />
    </div>
  );
};

export default Home;
